import React, { useState, useEffect, useMemo, useCallback, useRef, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useCollectionBindingOverrides,
  useEditMode,
  usePage,
  buildScopeContext,
  RichTextContent,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import type { Amenity } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import {
  hasCustomAmenityOrder,
  sortAmenities,
  toTitleCaseAmenity,
} from '../utils/sortAmenities';
import { GlowTrace } from './GlowTrace';

const OVERVIEW_AMENITY_TWO_COL_AT = 6;

// ============================================================================
// Slot Group Definitions
// ============================================================================

// Paths 1–2 keep legacy lifestyle_image_* keys so existing editor overrides still apply.
// Generators fill empty slots on Generate; editor VALUE overrides always win. Clear to skip.
const slide1Def: SlotGroupDefinition = {
  label: 'Slideshow Image 1',
  description: 'Slide 1 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'The strongest property photo for the opening overview slide — exterior, amenity, or lifestyle that best represents this community.',
    context: 'Slide 1 of 10 in the Property Overview slideshow on the Home page. Opening image. Do not reuse slides 2–10.',
  },
};

const slide2Def: SlotGroupDefinition = {
  label: 'Slideshow Image 2',
  description: 'Slide 2 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused strong property photo — different scene from slide 1 (interior, amenity, or lifestyle).',
    context: 'Slide 2 of 10 in the Property Overview slideshow on the Home page. Different photo from slides 1 and 3–10.',
  },
};

const slide3Def: SlotGroupDefinition = {
  label: 'Slideshow Image 3',
  description: 'Slide 3 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 3 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide4Def: SlotGroupDefinition = {
  label: 'Slideshow Image 4',
  description: 'Slide 4 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 4 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide5Def: SlotGroupDefinition = {
  label: 'Slideshow Image 5',
  description: 'Slide 5 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 5 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide6Def: SlotGroupDefinition = {
  label: 'Slideshow Image 6',
  description: 'Slide 6 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 6 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide7Def: SlotGroupDefinition = {
  label: 'Slideshow Image 7',
  description: 'Slide 7 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 7 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide8Def: SlotGroupDefinition = {
  label: 'Slideshow Image 8',
  description: 'Slide 8 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 8 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide9Def: SlotGroupDefinition = {
  label: 'Slideshow Image 9',
  description: 'Slide 9 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 9 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};

const slide10Def: SlotGroupDefinition = {
  label: 'Slideshow Image 10',
  description: 'Slide 10 of the overview slideshow. Generate fills empties; clear or override in the editor. Empty slots are not shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
  generator: {
    prompt: 'Another unused property photo that belongs in the overview slideshow.',
    context: 'Slide 10 of 10 in the Property Overview slideshow on the Home page. Different photo from the other slides. Leave empty if no strong unused photo remains.',
  },
};


const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Image Slider Sub-component
// ============================================================================

function slideLayerClass(isActive: boolean) {
  return `absolute inset-0 transition-opacity duration-600 ease-in-out ${
    isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
  }`;
}

interface OverviewSlideValues {
  image: string;
}

interface FilledSlide {
  key: string;
  image: string;
}

function GallerySlider({
  slide1,
  slide2,
  slide3,
  slide4,
  slide5,
  slide6,
  slide7,
  slide8,
  slide9,
  slide10,
}: {
  slide1: OverviewSlideValues;
  slide2: OverviewSlideValues;
  slide3: OverviewSlideValues;
  slide4: OverviewSlideValues;
  slide5: OverviewSlideValues;
  slide6: OverviewSlideValues;
  slide7: OverviewSlideValues;
  slide8: OverviewSlideValues;
  slide9: OverviewSlideValues;
  slide10: OverviewSlideValues;
}) {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const filled = useMemo<FilledSlide[]>(() => {
    const items: FilledSlide[] = [];
    const candidates = [
      { key: 'propertyoverview.lifestyle_image_1', ...slide1 },
      { key: 'propertyoverview.lifestyle_image_2', ...slide2 },
      { key: 'propertyoverview.slide_3', ...slide3 },
      { key: 'propertyoverview.slide_4', ...slide4 },
      { key: 'propertyoverview.slide_5', ...slide5 },
      { key: 'propertyoverview.slide_6', ...slide6 },
      { key: 'propertyoverview.slide_7', ...slide7 },
      { key: 'propertyoverview.slide_8', ...slide8 },
      { key: 'propertyoverview.slide_9', ...slide9 },
      { key: 'propertyoverview.slide_10', ...slide10 },
    ];
    for (const c of candidates) {
      if (c.image) items.push(c);
    }
    return items;
  }, [
    slide1, slide2, slide3, slide4, slide5, slide6,
    slide7, slide8, slide9, slide10,
  ]);

  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = filled.length;

  const goTo = useCallback((idx: number) => {
    if (isTransitioning || count <= 1) return;
    setIsTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, count]);

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  useEffect(() => {
    if (count > 0 && current >= count) setCurrent(0);
  }, [count, current]);

  const activeKey = filled[current]?.key;

  const emptyPill = (label: string) => (
    <span
      className={`inline-flex items-center rounded-full bg-white/90 px-3 py-1.5 font-body text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--vibe-color-text)] shadow-sm ${
        isEditMode ? '' : 'sr-only'
      }`}
    >
      {isEditMode ? `Add ${label.toLowerCase()}` : label}
    </span>
  );

  // Stack edit-mode pills for the first few empty slots only
  let emptyStack = 0;
  const emptyWrap = (hasImage: boolean, node: React.ReactNode) => {
    if (hasImage) return node;
    const stack = emptyStack++;
    if (!isEditMode || stack > 2) {
      return (
        <div className="absolute inset-0 pointer-events-none opacity-0" aria-hidden>
          {node}
        </div>
      );
    }
    return (
      <div
        className="absolute left-3 z-20"
        style={{ top: `${0.75 + stack * 2.5}rem` }}
      >
        {node}
      </div>
    );
  };

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm group">
      {!count && <div className="w-full h-full bg-[var(--vibe-color-surface)]" />}

      {emptyWrap(
        !!slide1.image,
        slide1.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.lifestyle_image_1')}>
            <EditableSlotGroup
              groupPath="propertyoverview.lifestyle_image_1"
              definition={slide1Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide1.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.lifestyle_image_1" definition={slide1Def}>
            {emptyPill('Slideshow Image 1')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide2.image,
        slide2.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.lifestyle_image_2')}>
            <EditableSlotGroup
              groupPath="propertyoverview.lifestyle_image_2"
              definition={slide2Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide2.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.lifestyle_image_2" definition={slide2Def}>
            {emptyPill('Slideshow Image 2')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide3.image,
        slide3.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_3')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_3"
              definition={slide3Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide3.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_3" definition={slide3Def}>
            {emptyPill('Slideshow Image 3')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide4.image,
        slide4.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_4')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_4"
              definition={slide4Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide4.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_4" definition={slide4Def}>
            {emptyPill('Slideshow Image 4')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide5.image,
        slide5.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_5')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_5"
              definition={slide5Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide5.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_5" definition={slide5Def}>
            {emptyPill('Slideshow Image 5')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide6.image,
        slide6.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_6')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_6"
              definition={slide6Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide6.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_6" definition={slide6Def}>
            {emptyPill('Slideshow Image 6')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide7.image,
        slide7.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_7')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_7"
              definition={slide7Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide7.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_7" definition={slide7Def}>
            {emptyPill('Slideshow Image 7')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide8.image,
        slide8.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_8')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_8"
              definition={slide8Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide8.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_8" definition={slide8Def}>
            {emptyPill('Slideshow Image 8')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide9.image,
        slide9.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_9')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_9"
              definition={slide9Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide9.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_9" definition={slide9Def}>
            {emptyPill('Slideshow Image 9')}
          </EditableSlotGroup>
        ),
      )}

      {emptyWrap(
        !!slide10.image,
        slide10.image ? (
          <div className={slideLayerClass(activeKey === 'propertyoverview.slide_10')}>
            <EditableSlotGroup
              groupPath="propertyoverview.slide_10"
              definition={slide10Def}
              className="block h-full w-full"
            >
              <VibesitesImage
                url={slide10.image}
                alt=""
                className="w-full h-full object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </EditableSlotGroup>
          </div>
        ) : (
          <EditableSlotGroup groupPath="propertyoverview.slide_10" definition={slide10Def}>
            {emptyPill('Slideshow Image 10')}
          </EditableSlotGroup>
        ),
      )}


      {/* Brand-tinted overlay at bottom for depth */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

      {count > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Previous image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Next image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PropertyOverview() {
  const description = useContentToken('propertyoverview.description');
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const slide1 = useSlotGroup('propertyoverview.lifestyle_image_1', slide1Def);
  const slide2 = useSlotGroup('propertyoverview.lifestyle_image_2', slide2Def);
  const slide3 = useSlotGroup('propertyoverview.slide_3', slide3Def);
  const slide4 = useSlotGroup('propertyoverview.slide_4', slide4Def);
  const slide5 = useSlotGroup('propertyoverview.slide_5', slide5Def);
  const slide6 = useSlotGroup('propertyoverview.slide_6', slide6Def);
  const slide7 = useSlotGroup('propertyoverview.slide_7', slide7Def);
  const slide8 = useSlotGroup('propertyoverview.slide_8', slide8Def);
  const slide9 = useSlotGroup('propertyoverview.slide_9', slide9Def);
  const slide10 = useSlotGroup('propertyoverview.slide_10', slide10Def);
  const tabInteriorLabel = useContentToken('propertyoverview.tab_interior_label');
  const tabCommunityLabel = useContentToken('propertyoverview.tab_community_label');

  // Reuse AmenitiesList binding paths so overview order matches the Amenities page
  // (including editor custom order / hide overrides).
  const page = usePage();
  const scopeCtx = buildScopeContext(page);
  const collectionOverrides = useCollectionBindingOverrides();
  const communityCustomOrder = hasCustomAmenityOrder(
    resolveScopedOverride(collectionOverrides, 'amenitieslist.communityItems', scopeCtx),
  );
  const apartmentCustomOrder = hasCustomAmenityOrder(
    resolveScopedOverride(collectionOverrides, 'amenitieslist.apartmentItems', scopeCtx),
  );

  const communityAmenitiesRaw = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentAmenitiesRaw = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  // Featured / desirability sort by default; Amenities-page editor reorder wins when present.
  const communityAmenities = useMemo(
    () => sortAmenities(communityAmenitiesRaw, { customOrder: communityCustomOrder }),
    [communityAmenitiesRaw, communityCustomOrder],
  );
  const apartmentAmenities = useMemo(
    () => sortAmenities(apartmentAmenitiesRaw, { customOrder: apartmentCustomOrder }),
    [apartmentAmenitiesRaw, apartmentCustomOrder],
  );

  const showInteriorSelectHomesDisclaimer = useMemo(
    () => apartmentAmenities.some((item) => item.title?.includes('*')),
    [apartmentAmenities],
  );
  const interiorSelectHomesDisclaimer = useContentToken('propertyoverview.interior_select_homes_disclaimer');

  const [activeTab, setActiveTab] = useState<'interior' | 'community'>('interior');
  const [descExpanded, setDescExpanded] = useState(false);
  const activeItems = activeTab === 'interior' ? apartmentAmenities : communityAmenities;
  const useTwoColAmenities = activeItems.length >= OVERVIEW_AMENITY_TWO_COL_AT;

  useRevealStyles();
  const accentRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* ============================================================ */}
          {/* Left Column: Text + Amenity Tabs                              */}
          {/* ============================================================ */}
          <div ref={accentRef as RefObject<HTMLDivElement>} className="reveal-fade-up">
            {/* Accent line draws in above the eyebrow */}
            <svg className="accent-line accent-line--delay-1 mb-4" style={{ '--line-length': '64', width: '64px', height: '3px' } as React.CSSProperties} viewBox="0 0 64 3" preserveAspectRatio="none">
              <line x1="0" y1="1.5" x2="64" y2="1.5" strokeWidth="2" />
            </svg>

            <RichTextContent
              path="propertyoverview.pre_title"
              label="Eyebrow Text"
              defaultValue="<p>Your Search Ends Here</p>"
              as="p"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--vibe-color-primary)] mb-3"
            />

            <RichTextContent
              path="propertyoverview.title"
              label="Title"
              defaultValue="<p>Experience the charm.</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-normal text-[var(--vibe-color-text)] mb-5 leading-[1.15]"
            />

            <div className="mb-10 max-w-lg">
              <div className={`overview-desc-wrap ${descExpanded ? 'overview-desc-wrap--open' : ''}`}>
                <RichTextContent
                  path="propertyoverview.description"
                  label="Description"
                  defaultValue="<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities. Discover the perfect blend of comfort, style, and convenience.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed"
                />
              </div>
              {description && String(description).length > 200 && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="mt-2 font-body text-sm font-semibold text-[var(--vibe-color-text)] hover:underline transition-colors"
                >
                  {descExpanded ? (
                    <RichTextContent
                      path="propertyoverview.read_less_label"
                      label="Read Less Label"
                      defaultValue="<p>Read Less</p>"
                      as="span"
                    />
                  ) : (
                    <RichTextContent
                      path="propertyoverview.read_more_label"
                      label="Read More Label"
                      defaultValue="<p>Read More</p>"
                      as="span"
                    />
                  )}
                </button>
              )}
            </div>

            {/* Amenity Tabs */}
            {(apartmentAmenities.length > 0 || communityAmenities.length > 0) && (
              <div className="mb-8 max-w-lg">
                <div className="overview-tab-row flex flex-wrap gap-2">
                  {apartmentAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('interior')}
                      className={`overview-tab ${activeTab === 'interior' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_interior_label" defaultValue="Interior Features" as="span">
                        {tabInteriorLabel}
                      </EditableToken>
                    </button>
                  )}
                  {communityAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('community')}
                      className={`overview-tab ${activeTab === 'community' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_community_label" defaultValue="Community Amenities" as="span">
                        {tabCommunityLabel}
                      </EditableToken>
                    </button>
                  )}
                </div>

                <div className="pt-4">
                  <ul
                    className={
                      useTwoColAmenities
                        ? 'overview-amenity-list overview-amenity-list--two-col'
                        : 'overview-amenity-list'
                    }
                  >
                    {activeItems.map((a, i) => (
                      <li key={`${activeTab}-${i}`} className="overview-amenity-item flex items-start gap-2.5 text-sm font-body text-[var(--vibe-color-text-muted)] leading-snug">
                        <span className="overview-bullet mt-1.5 flex-shrink-0" />
                        <span>{a.title ? toTitleCaseAmenity(a.title) : ''}</span>
                      </li>
                    ))}
                  </ul>
                  {activeTab === 'interior' && showInteriorSelectHomesDisclaimer && (
                    <EditableToken
                      path="propertyoverview.interior_select_homes_disclaimer"
                      as="p"
                      className="overview-amenity-disclaimer"
                    >
                      {interiorSelectHomesDisclaimer || '* Available in select homes'}
                    </EditableToken>
                  )}
                </div>
              </div>
            )}

            {/* CTA */}
            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta-btn inline-flex items-center gap-2"
              >
                <span>{ctaLink.label || 'See All Availability'}</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>

          {/* ============================================================ */}
          {/* Right Column: Dedicated image slots only                      */}
          {/* ============================================================ */}
          <div className="glow-trace-wrap relative w-full aspect-[4/3] lg:aspect-[3/4] lg:max-h-[640px] rounded-sm overflow-hidden">
            <GlowTrace delay={0.5} />
            <GallerySlider
              slide1={{ image: slide1.image || "" }}
              slide2={{ image: slide2.image || "" }}
              slide3={{ image: slide3.image || "" }}
              slide4={{ image: slide4.image || "" }}
              slide5={{ image: slide5.image || "" }}
              slide6={{ image: slide6.image || "" }}
              slide7={{ image: slide7.image || "" }}
              slide8={{ image: slide8.image || "" }}
              slide9={{ image: slide9.image || "" }}
              slide10={{ image: slide10.image || "" }}
            />
          </div>
        </div>
      </div>

      <style>{`
        /* Description expand / collapse */
        .overview-desc-wrap {
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .overview-desc-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* Tabs */
        .overview-tab {
          padding: 0.5rem 1.25rem;
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .overview-tab:hover {
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
        .overview-tab--active {
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 78%, black) 100%
          );
          border-color: transparent;
          box-shadow: 0 1px 6px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .overview-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: transparent;
        }

        /* Bullet */
        .overview-bullet {
          display: block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
          opacity: 0.6;
          flex-shrink: 0;
        }

        /* Amenity list — multi-column so wrapped lines don't stretch sibling rows */
        .overview-amenity-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .overview-amenity-list--two-col {
          display: block;
          column-count: 1;
          column-gap: 1rem;
        }
        @media (min-width: 640px) {
          .overview-amenity-list--two-col {
            column-count: 2;
          }
        }
        .overview-amenity-item {
          break-inside: avoid;
          -webkit-column-break-inside: avoid;
          page-break-inside: avoid;
          margin-bottom: 0.5rem;
        }
        .overview-amenity-list--two-col .overview-amenity-item:last-child {
          margin-bottom: 0;
        }

        .overview-amenity-disclaimer {
          margin: 0.75rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.45;
          color: var(--vibe-color-text-muted);
        }

        /* CTA Button */
        .overview-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .overview-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }

        /* Slider transition */
        .duration-600 {
          transition-duration: 600ms;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Eyebrow Text',
      type: 'richtext',
      defaultValue: '<p>Your Search Ends Here</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_hero_title',
      defaultValue: '<p>Experience the charm.</p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities.</p>',
    },
    'propertyoverview.tab_interior_label': {
      label: 'Interior Tab Label',
      type: 'text',
      defaultValue: 'Interior Features',
    },
    'propertyoverview.tab_community_label': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'propertyoverview.interior_select_homes_disclaimer': {
      label: 'Interior Select Homes Disclaimer',
      type: 'text',
      defaultValue: '* Available in select homes',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.lifestyle_image_1': slide1Def,
    'propertyoverview.lifestyle_image_2': slide2Def,
    'propertyoverview.slide_3': slide3Def,
    'propertyoverview.slide_4': slide4Def,
    'propertyoverview.slide_5': slide5Def,
    'propertyoverview.slide_6': slide6Def,
    'propertyoverview.slide_7': slide7Def,
    'propertyoverview.slide_8': slide8Def,
    'propertyoverview.slide_9': slide9Def,
    'propertyoverview.slide_10': slide10Def,
    'propertyoverview.cta_link': ctaLinkDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
